'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { LinkButton } from '@/components/ui/Button/Button';
import { TrackingEvent } from '@/utils/helpers';
import { analytics } from '@/lib/analytics';
interface Props {
  trigger: ReactNode;
  message: string | ReactNode;
  title: string;
  emailMessage?: string;
  primaryLink?: {
    href: string;
    text: string;
    target?: string;
    onClick?: () => void;
  };
  secondaryAction?: ReactNode;
  tracking?: TrackingEvent;
}

export const InstructModal = ({
  trigger,
  message,
  title,
  primaryLink,
  secondaryAction,
  tracking,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (isOpen && tracking) {
      analytics.capture(tracking.event, tracking.properties);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <button onClick={toggleModal} className={'text-white'}>
        {trigger}
      </button>
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
          >
            <span className="flex justify-between items-center p-4 border-zinc-700">
              <span className="text-xl font-semibold">{title}</span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>
            <span className="p-4 flex flex-col gap-6">
              {typeof message === 'string' ? (
                <span className="text-zinc-400 text-left">{message}</span>
              ) : (
                message
              )}
              <span className={'flex flex-col gap-4'}>
                {secondaryAction && secondaryAction}
                {primaryLink && (
                  <LinkButton
                    href={primaryLink.href}
                    variant={'slim'}
                    isPrimary={true}
                    onClick={() => {
                      primaryLink.onClick && primaryLink.onClick();
                      toggleModal();
                    }}
                    target={primaryLink.target ? primaryLink.target : '_self'}
                  >
                    {primaryLink.text}
                  </LinkButton>
                )}
              </span>
            </span>
          </span>
        </span>
      )}
    </>
  );
};
